import React, { useState, useEffect } from 'react';
import AuthorFields from '../components/AuthorFields';
import CitationResult from '../components/CitationResult';
import { formatAuthorsForCitation, formatAuthorsForParenthetical, formatAuthorsForReference, formatURL, formatDOI } from '../utils/citationHelpers';

function JournalForm() {
  const [authors, setAuthors] = useState([]);
  const [authorType, setAuthorType] = useState('personal');
  const [formData, setFormData] = useState({
    title: '',
    journal: '',
    year: '',
    volume: '',
    issue: '',
    pages: '',
    urlOrDoi: ''
  });
  const [result, setResult] = useState(null);
  const [liveResult, setLiveResult] = useState(null);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Genera una vista previa en tiempo real
  useEffect(() => {
    if (authors.length > 0 && formData.title && formData.journal && formData.year) {
      generatePreview();
    } else {
      setLiveResult(null);
    }
  }, [authors, formData]);

  const generatePreview = () => {
    // Verificar si hay autores institucionales con siglas
    const institutionalAuthors = authors.filter(
      author => author.type === 'institutional' && author.abbreviation
    );

    let narrativeCitation, parentheticalCitation, narrativeSubsequent, parentheticalSubsequent;

    if (institutionalAuthors.length === 1 && (authorType === 'institutional' || authorType === 'both')) {
      // Para cita narrativa con institucional con sigla: "Nombre (SIGLA, año)"
      narrativeCitation = `${institutionalAuthors[0].name} (${institutionalAuthors[0].abbreviation}, ${formData.year})`;
      
      // Para cita parentética: "(Nombre [SIGLA], año)"
      parentheticalCitation = `(${institutionalAuthors[0].name} [${institutionalAuthors[0].abbreviation}], ${formData.year})`;
      
      // Para cita narrativa subsecuente: "SIGLA (año)"
      narrativeSubsequent = `${institutionalAuthors[0].abbreviation} (${formData.year})`;
      
      // Para cita parentética subsecuente: "(SIGLA, año)"
      parentheticalSubsequent = `(${institutionalAuthors[0].abbreviation}, ${formData.year})`;
    } else {
      // Caso normal sin autor institucional con sigla o múltiples autores
      const authorsForNarrative = formatAuthorsForCitation(authors, authorType);
      const authorsForParenthetical = formatAuthorsForParenthetical(authors, authorType);
      const authorsForNarrativeSubsequent = formatAuthorsForCitation(authors, authorType, false);
      const authorsForParentheticalSubsequent = formatAuthorsForParenthetical(authors, authorType, false);

      narrativeCitation = `${authorsForNarrative} (${formData.year})`;
      parentheticalCitation = `(${authorsForParenthetical}, ${formData.year})`;
      narrativeSubsequent = `${authorsForNarrativeSubsequent} (${formData.year})`;
      parentheticalSubsequent = `(${authorsForParentheticalSubsequent}, ${formData.year})`;
    }

    const citation = {
      narrative: narrativeCitation,
      parenthetical: parentheticalCitation,
      narrativeSubsequent: narrativeSubsequent,
      parentheticalSubsequent: parentheticalSubsequent
    };

    const authorsForReference = formatAuthorsForReference(authors, authorType);

    let formattedUrlOrDoi = '';
    if (formData.urlOrDoi) {
      // Comprobar si es un DOI por patrón o prefijo
      if (formData.urlOrDoi.toLowerCase().includes('doi') || /^10\.\d{4,}\/\S+$/.test(formData.urlOrDoi)) {
        formattedUrlOrDoi = formatDOI(formData.urlOrDoi);
      } else {
        formattedUrlOrDoi = formatURL(formData.urlOrDoi);
      }
    }

    let referenceText = `${authorsForReference} (${formData.year}). ${formData.title}. <em>${formData.journal}</em>`;

    if (formData.volume) {
      referenceText += `, <em>${formData.volume}</em>`;
      if (formData.issue) {
        referenceText += `(${formData.issue})`;
      }
    } else if (formData.issue) {
      // Si hay número pero no volumen
      referenceText += `, (${formData.issue})`;
    }

    if (formData.pages) {
      referenceText += `, ${formData.pages}`;
    }

    if (formattedUrlOrDoi) {
      referenceText += `. ${formattedUrlOrDoi}`;
    } else {
      referenceText += '.';
    }

    const reference = {
      html: referenceText,
      text: referenceText.replace(/<[^>]*>/g, '')
    };

    setLiveResult({ citation, reference });
  };

  const generateCitation = () => {
    if (authors.length === 0 || !formData.title || !formData.journal || !formData.year) {
      alert('Generador APA, séptima edición dice:\n\nPor favor, completa todos los campos obligatorios.');
      return;
    }

    // Verificar si hay autores institucionales con siglas
    const institutionalAuthors = authors.filter(
      author => author.type === 'institutional' && author.abbreviation
    );

    let narrativeCitation, parentheticalCitation, narrativeSubsequent, parentheticalSubsequent;

    if (institutionalAuthors.length === 1 && (authorType === 'institutional' || authorType === 'both')) {
      // Para cita narrativa con institucional con sigla: "Nombre (SIGLA, año)"
      narrativeCitation = `${institutionalAuthors[0].name} (${institutionalAuthors[0].abbreviation}, ${formData.year})`;
      
      // Para cita parentética: "(Nombre [SIGLA], año)"
      parentheticalCitation = `(${institutionalAuthors[0].name} [${institutionalAuthors[0].abbreviation}], ${formData.year})`;
      
      // Para cita narrativa subsecuente: "SIGLA (año)"
      narrativeSubsequent = `${institutionalAuthors[0].abbreviation} (${formData.year})`;
      
      // Para cita parentética subsecuente: "(SIGLA, año)"
      parentheticalSubsequent = `(${institutionalAuthors[0].abbreviation}, ${formData.year})`;
    } else {
      // Caso normal sin autor institucional con sigla o múltiples autores
      const authorsForNarrative = formatAuthorsForCitation(authors, authorType);
      const authorsForParenthetical = formatAuthorsForParenthetical(authors, authorType);
      const authorsForNarrativeSubsequent = formatAuthorsForCitation(authors, authorType, false);
      const authorsForParentheticalSubsequent = formatAuthorsForParenthetical(authors, authorType, false);

      narrativeCitation = `${authorsForNarrative} (${formData.year})`;
      parentheticalCitation = `(${authorsForParenthetical}, ${formData.year})`;
      narrativeSubsequent = `${authorsForNarrativeSubsequent} (${formData.year})`;
      parentheticalSubsequent = `(${authorsForParentheticalSubsequent}, ${formData.year})`;
    }

    const citation = {
      narrative: narrativeCitation,
      parenthetical: parentheticalCitation,
      narrativeSubsequent: narrativeSubsequent,
      parentheticalSubsequent: parentheticalSubsequent
    };

    const authorsForReference = formatAuthorsForReference(authors, authorType);

    let formattedUrlOrDoi = '';
    if (formData.urlOrDoi) {
      // Comprobar si es un DOI por patrón o prefijo
      if (formData.urlOrDoi.toLowerCase().includes('doi') || /^10\.\d{4,}\/\S+$/.test(formData.urlOrDoi)) {
        formattedUrlOrDoi = formatDOI(formData.urlOrDoi);
      } else {
        formattedUrlOrDoi = formatURL(formData.urlOrDoi);
      }
    }

    let referenceText = `${authorsForReference} (${formData.year}). ${formData.title}. <em>${formData.journal}</em>`;

    if (formData.volume) {
      referenceText += `, <em>${formData.volume}</em>`;
      if (formData.issue) {
        referenceText += `(${formData.issue})`;
      }
    } else if (formData.issue) {
      // Si hay número pero no volumen
      referenceText += `, (${formData.issue})`;
    }

    if (formData.pages) {
      referenceText += `, ${formData.pages}`;
    }

    if (formattedUrlOrDoi) {
      referenceText += `. ${formattedUrlOrDoi}`;
    } else {
      referenceText += '.';
    }

    const reference = {
      html: referenceText,
      text: referenceText.replace(/<[^>]*>/g, '')
    };

    setResult({ citation, reference });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 dark:text-gray-100">Artículo de revista</h1>
        <p className="text-gray-600 dark:text-gray-400">Artículos de revistas académicas o científicas</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 sm:gap-8">
        <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-5 dark:bg-gray-800 dark:border-gray-700">
          <div className="space-y-3 sm:space-y-4">
            <AuthorFields
              authors={authors}
              setAuthors={setAuthors}
              authorType={authorType}
              setAuthorType={setAuthorType}
            />

            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Título del artículo *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                required
              />
            </div>

            <div>
              <label htmlFor="journal" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Nombre de la revista *
              </label>
              <input
                type="text"
                id="journal"
                name="journal"
                value={formData.journal}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                  Año de publicación *
                </label>
                <input
                  type="number"
                  id="year"
                  name="year"
                  value={formData.year}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  required
                />
              </div>

              <div>
                <label htmlFor="volume" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                  Volumen
                </label>
                <input
                  type="text"
                  id="volume"
                  name="volume"
                  value={formData.volume}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label htmlFor="issue" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                  Número
                </label>
                <input
                  type="text"
                  id="issue"
                  name="issue"
                  value={formData.issue}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                />
              </div>

              <div>
                <label htmlFor="pages" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                  Páginas (ej: 15-30)
                </label>
                <input
                  type="text"
                  id="pages"
                  name="pages"
                  value={formData.pages}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  placeholder="15-30"
                />
              </div>
            </div>

            <div>
              <label htmlFor="urlOrDoi" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                URL o DOI
              </label>
              <input
                type="text"
                id="urlOrDoi"
                name="urlOrDoi"
                value={formData.urlOrDoi}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                placeholder="URL o número DOI (10.xxxx/xxxxx)"
              />
              <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                Puede introducir una URL completa, un enlace sin https://, o un DOI con o sin el prefijo https://doi.org/
              </p>
            </div>

            <button
              type="button"
              onClick={generateCitation}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              Generar cita y referencia
            </button>
          </div>
        </div>

        <div className="space-y-6">
          {!result && liveResult && (
            <CitationResult
              citation={liveResult.citation}
              reference={liveResult.reference}
              livePreview={true}
            />
          )}

          <CitationResult
            citation={result?.citation}
            reference={result?.reference}
          />
        </div>
      </div>
    </div>
  );
}

export default JournalForm;